import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './GestionElevesClasse.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';

const API_BASE_URL = 'http://127.0.0.1:8000/api/v1';

function GestionElevesClasse() {
  const [classes, setClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [eleves, setEleves] = useState([]);
  const [tousEleves, setTousEleves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchClasses();
    fetchTousEleves();
  }, []);

  useEffect(() => {
    if (selectedClasse) {
      fetchElevesClasse();
    }
  }, [selectedClasse]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/classes`);
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors du chargement des classes');
    }
  };

  const fetchTousEleves = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/etudiants`);
      const elevesData = response.data.etudiants || response.data.data || [];
      setTousEleves(elevesData);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchElevesClasse = async () => {
    try {
      setLoading(true);
      const classeNom = classes.find(c => c.id === parseInt(selectedClasse))?.nom;
      if (!classeNom) return;

      const response = await axios.get(`${API_BASE_URL}/admin/classes/${encodeURIComponent(classeNom)}/etudiants`);
      setEleves(response.data.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors du chargement des élèves');
      setEleves([]);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleAssignerEleve = async (eleveId) => {
    const classeNom = classes.find(c => c.id === parseInt(selectedClasse))?.nom;
    if (!classeNom) return;

    try {
      await axios.put(`${API_BASE_URL}/admin/etudiants/${eleveId}`, {
        classe: classeNom
      });

      showMessage('success', 'Élève assigné avec succès');
      fetchElevesClasse();
      fetchTousEleves();
      setShowModal(false);
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', error.response?.data?.message || 'Erreur lors de l\'assignation');
    }
  };

  const handleRetirerEleve = async (eleveId) => {
    if (!window.confirm('Voulez-vous vraiment retirer cet élève de cette classe?')) {
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/admin/etudiants/${eleveId}`, {
        classe: null
      });

      showMessage('success', 'Élève retiré avec succès');
      fetchElevesClasse();
      fetchTousEleves();
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', error.response?.data?.message || 'Erreur lors du retrait');
    }
  };

  const getElevesSansClasse = () => {
    return tousEleves.filter(e => !e.classe || e.classe === '');
  };

  const getElevesAutresClasses = () => {
    const classeNom = classes.find(c => c.id === parseInt(selectedClasse))?.nom;
    return tousEleves.filter(e => e.classe && e.classe !== classeNom);
  };

  const getFilteredEleves = (elevesList) => {
    if (!searchTerm) return elevesList;
    
    return elevesList.filter(e => 
      `${e.prenom} ${e.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.matricule?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const selectedClasseInfo = classes.find(c => c.id === parseInt(selectedClasse));

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Élèves par Classe" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="gestion-eleves-classe-container">
            
            {message.text && (
              <div className={`alert-message ${message.type}`}>
                <span className="alert-icon">{message.type === 'success' ? '✅' : '❌'}</span>
                <span>{message.text}</span>
              </div>
            )}

            {/* Sélection de la classe */}
            <div className="classe-selector-card">
              <h2>Sélectionner une classe</h2>
              <select
                value={selectedClasse}
                onChange={(e) => setSelectedClasse(e.target.value)}
                className="classe-select"
              >
                <option value="">-- Choisir une classe --</option>
                {classes.map(classe => (
                  <option key={classe.id} value={classe.id}>
                    {classe.nom} ({classe.niveau}) - {classe.effectif}/{classe.capacite} élèves
                  </option>
                ))}
              </select>
            </div>

            {selectedClasse && selectedClasseInfo && (
              <>
                {/* Informations de la classe */}
                <div className="classe-info-card">
                  <div className="classe-info-header">
                    <h2>{selectedClasseInfo.nom}</h2>
                    <span className="niveau-badge">{selectedClasseInfo.niveau}</span>
                  </div>
                  <div className="classe-stats-row">
                    <div className="stat-item">
                      <span className="stat-icon">👥</span>
                      <div>
                        <div className="stat-value">{selectedClasseInfo.effectif} / {selectedClasseInfo.capacite}</div>
                        <div className="stat-label">Élèves</div>
                      </div>
                    </div>
                    <div className="stat-item">
                      <span className="stat-icon">📊</span>
                      <div>
                        <div className="stat-value">{selectedClasseInfo.taux_remplissage}%</div>
                        <div className="stat-label">Remplissage</div>
                      </div>
                    </div>
                    <div className="stat-item">
                      <span className="stat-icon">📍</span>
                      <div>
                        <div className="stat-value">{selectedClasseInfo.capacite - selectedClasseInfo.effectif}</div>
                        <div className="stat-label">Places disponibles</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Liste des élèves de la classe */}
                <div className="eleves-section">
                  <div className="section-header">
                    <h3>Élèves de la classe ({eleves.length})</h3>
                    <button 
                      className="btn-add-eleve"
                      onClick={() => setShowModal(true)}
                    >
                      ➕ Ajouter un élève
                    </button>
                  </div>

                  {loading ? (
                    <div className="loading-state">
                      <div className="spinner"></div>
                      <p>Chargement des élèves...</p>
                    </div>
                  ) : eleves.length === 0 ? (
                    <div className="empty-state">
                      <span className="empty-icon">👥</span>
                      <h3>Aucun élève dans cette classe</h3>
                      <p>Cliquez sur "Ajouter un élève" pour commencer</p>
                    </div>
                  ) : (
                    <div className="eleves-grid">
                      {eleves.map(eleve => (
                        <div key={eleve.id} className="eleve-card">
                          <div className="eleve-avatar">
                            {eleve.prenom?.charAt(0)}{eleve.nom?.charAt(0)}
                          </div>
                          <div className="eleve-info">
                            <h4>{eleve.prenom} {eleve.nom}</h4>
                            <p className="eleve-matricule">{eleve.matricule}</p>
                            <p className="eleve-email">{eleve.email}</p>
                          </div>
                          <button
                            className="btn-retirer"
                            onClick={() => handleRetirerEleve(eleve.id)}
                            title="Retirer de la classe"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {!selectedClasse && (
              <div className="instruction-card">
                <span className="instruction-icon">💡</span>
                <div>
                  <h3>Comment gérer les élèves par classe ?</h3>
                  <ol>
                    <li>Sélectionnez une classe dans la liste déroulante</li>
                    <li>Consultez la liste des élèves de cette classe</li>
                    <li>Ajoutez ou retirez des élèves selon vos besoins</li>
                  </ol>
                  <p className="note">
                    <strong>Note:</strong> Le système charge automatiquement les élèves en fonction 
                    du champ "classe" dans leur profil. Cette interface vous permet de gérer 
                    manuellement les assignations.
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal Ajout d'élève */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajouter un élève à {selectedClasseInfo?.nom}</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Rechercher un élève..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="eleves-tabs">
                <h3>Élèves sans classe ({getElevesSansClasse().length})</h3>
                <div className="eleves-list">
                  {getFilteredEleves(getElevesSansClasse()).map(eleve => (
                    <div key={eleve.id} className="eleve-item">
                      <div className="eleve-avatar-small">
                        {eleve.prenom?.charAt(0)}{eleve.nom?.charAt(0)}
                      </div>
                      <div className="eleve-info-small">
                        <strong>{eleve.prenom} {eleve.nom}</strong>
                        <span>{eleve.matricule}</span>
                      </div>
                      <button
                        className="btn-assigner"
                        onClick={() => handleAssignerEleve(eleve.id)}
                      >
                        ➕ Ajouter
                      </button>
                    </div>
                  ))}
                  {getFilteredEleves(getElevesSansClasse()).length === 0 && (
                    <p className="no-results">Aucun élève sans classe trouvé</p>
                  )}
                </div>

                <h3>Élèves d'autres classes ({getElevesAutresClasses().length})</h3>
                <div className="eleves-list">
                  {getFilteredEleves(getElevesAutresClasses()).map(eleve => (
                    <div key={eleve.id} className="eleve-item">
                      <div className="eleve-avatar-small">
                        {eleve.prenom?.charAt(0)}{eleve.nom?.charAt(0)}
                      </div>
                      <div className="eleve-info-small">
                        <strong>{eleve.prenom} {eleve.nom}</strong>
                        <span>{eleve.matricule} - {eleve.classe}</span>
                      </div>
                      <button
                        className="btn-assigner"
                        onClick={() => handleAssignerEleve(eleve.id)}
                      >
                        ↔️ Déplacer
                      </button>
                    </div>
                  ))}
                  {getFilteredEleves(getElevesAutresClasses()).length === 0 && (
                    <p className="no-results">Aucun élève dans d'autres classes trouvé</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionElevesClasse;
